/* shader-template.ts - validate and expand hierarchical shader snippets. */

/**
 * Parses authored snippet contracts once and checks expansion data before
 * emitting GLSL. Parameters have lexical scope; children use local data.
 * Formatting belongs to the template and is preserved during expansion.
 * Counters are integers the template declares; a snippet reads or steps
 * one on entry, and a loop snippet repeats up to a counter's maximum.
 */
class ShaderTemplate {

    /** Parse block directives and validate every branch's parameter scope. */
    constructor(source: string) {

        const stack = [this.root_];
        const lines = source.match(/[^\n]*\n|[^\n]+$/g) ?? [];
        const declaration = new RegExp(
            '^([A-Za-z_][\\w-]*)(?:\\(([^)]*)\\))?'
            + '((?:[ \\t]+counter\\([^)]*\\))*)'
            + '(?:[ \\t]+(optional|multi(?: required)?|choice'
            + '|loop[ \\t]+max\\([A-Za-z_]\\w*\\)))?$');

        let commented = false;
        lines.forEach((text, index) => {

            const line = index + 1;
            const parent = stack[stack.length - 1];
            const path = stack.map(node => node.name).join('.');
            const fail = (message: string): never => {
                throw new Error(`${path} (line ${line}): ${message}`);
            };
            const directive = text.trimEnd().match(
                /^\s*(?:#pragma[ \t]+|\/\/%)(snip\w*|end|counter)\b(.*)$/);

            const insideComment = commented;
            commented = this.blockComment(text, commented);

            if (directive) {

                // commenting a region out must not leave its snippets behind
                if (insideComment) fail('directive inside a block comment');

                const [, command, argument] = directive;
                if (command === 'counter') {

                    const match = argument.trim().match(
                        /^([A-Za-z_]\w*)[ \t]+(\d+)$/);
                    if (!match || stack.length !== 1)
                        fail(`invalid counter declaration '${text.trim()}'`);
                    if (this.counters_.has(match![1]))
                        fail(`counter '${match![1]}' already declared`);
                    this.counters_.set(match![1], Number(match![2]));
                    return;
                }
                if (command === 'end') {

                    if (stack.length === 1 || argument.trim() !== parent.name)
                        fail(`unexpected end '${argument.trim()}'`);
                    stack.pop();
                }

                if (command !== 'end') {

                    const match = argument.trim().match(declaration);
                    if (command !== 'snip' || !match)
                        fail(`invalid snippet declaration '${text.trim()}'`);
                    const [, name, parameters, clauses, mode] = match!;
                    const loop = mode?.match(/^loop[ \t]+max\((\w+)\)$/);
                    const snippet: ShaderTemplate.Snippet = {
                        name, line, parts: [],
                        parameters: parameters?.trim()
                            ? parameters.split(',').map(name => name.trim())
                            : [],
                        bindings: [...clauses.matchAll(/counter\(([^)]*)\)/g)]
                            .map(clause => this.parseBinding(clause[1], fail)),
                        mode: (loop ? 'loop' : mode ?? 'one') as
                            ShaderTemplate.Mode,
                        loopCounter: loop?.[1],
                    };
                    parent.parts.push(snippet);
                    stack.push(snippet);
                }
                return;
            }

            // Substitutions are parsed once; their values are never reparsed.
            let offset = 0;
            for (const match of text.matchAll(/\$\{([^}]*)\}/g)) {

                parent.parts.push(text.slice(offset, match.index));
                parent.parts.push({ parameter: match[1], line });
                offset = match.index! + match[0].length;
            }
            const tail = text.slice(offset);
            if (tail.includes('${')) fail('unclosed parameter substitution');
            parent.parts.push(tail);
        });

        if (stack.length !== 1) {

            const node = stack[stack.length - 1];
            throw new Error(
                `${stack.map(node => node.name).join('.')} `
                + `(line ${node.line}): unclosed snippet`);
        }
        this.checkTemplate(this.root_, new Map(), 'root');
    }

    /**
     * Validate the complete selected tree before emitting any shader text.
     * Counters start at their declared values and change in emission order;
     * a counter value below zero throws.
     */
    expand(data: ShaderTemplate.Data): string {

        this.checkData(this.root_, data, 'root');
        const output: string[] = [];
        const counters = new Map(this.counters_);
        const maxima = new Map(this.counters_);
        const loops: {
            slot: number;
            node: ShaderTemplate.Snippet;
            scope: ShaderTemplate.Data;
        }[] = [];

        const emit = (
            node: ShaderTemplate.Snippet,
            record: ShaderTemplate.Data,
            inherited: ShaderTemplate.Data,
            path: string,
        ): void => {

            const scope = { ...inherited, ...record };
            for (const binding of node.bindings) {

                // the operand names a counter directly or through a parameter
                const name = counters.has(binding.operand)
                    ? binding.operand : String(scope[binding.operand]);
                const current = counters.get(name);
                if (current === undefined)
                    throw new Error(`${path}: unknown counter '${name}'`);

                let [value, next] = [0, 0];

                // post-increment: the alias receives the value before it
                if (binding.step === '++')
                    [value, next] = [current, current + 1];

                // pre-decrement: the alias receives the value after it
                if (binding.step === '--')
                    [value, next] = [current - 1, current - 1];

                // the alias receives the value minus one, no change
                if (binding.step === '-1')
                    [value, next] = [current - 1, current];

                if (value < 0)
                    throw new Error(`${path}: counter '${name}' below zero`);

                counters.set(name, next);
                maxima.set(name, Math.max(maxima.get(name)!, next));
                scope[binding.alias] = value;
            }

            for (const part of node.parts) {

                if (typeof part === 'string') output.push(part);
                if (typeof part !== 'string' && 'parameter' in part)
                    output.push(String(scope[part.parameter]));
                if (typeof part !== 'string' && 'parts' in part) {

                    // a loop is emitted once the counter maxima are final
                    if (part.mode === 'loop') {

                        loops.push({
                            slot: output.push('') - 1, node: part, scope });
                        continue;
                    }

                    const value = Object.prototype.hasOwnProperty.call(
                        record, part.name) ? record[part.name] : undefined;
                    for (const [index, child] of
                        this.instances(value).entries()) {

                        const at = part.mode.startsWith('multi')
                            ? `${path}.${part.name}[${index}]`
                            : `${path}.${part.name}`;
                        emit(part, child as ShaderTemplate.Data, scope, at);
                    }
                }
            }
        };

        emit(this.root_, this.instances(data)[0] as ShaderTemplate.Data, {},
            'root');

        // a loop body holds text and substitutions only
        for (const { slot, node, scope } of loops) {

            const count = maxima.get(node.loopCounter!)!;
            const body: string[] = [];
            for (let index = 0; index < count; index++) {

                const values = { ...scope, [node.parameters[0]]: index };
                for (const part of node.parts) {

                    if (typeof part === 'string') body.push(part);
                    if (typeof part !== 'string' && 'parameter' in part)
                        body.push(String(values[part.parameter]));
                }
            }
            output[slot] = body.join('');
        }
        return output.join('');
    }

    private checkTemplate(
        node: ShaderTemplate.Snippet,
        inherited: Map<string, { used: boolean }>,
        path: string,
    ): void {

        const fail = (message: string, line = node.line): never => {
            throw new Error(`${path} (line ${line}): ${message}`);
        };
        const bindings = new Map(inherited);
        const aliases = node.bindings.map(binding => binding.alias);
        for (const name of [...node.parameters, ...aliases]) {

            if (!/^[A-Za-z_]\w*$/.test(name))
                fail(`invalid parameter '${name}'`);
            if (bindings.has(name) || this.counters_.has(name))
                fail(`parameter '${name}' already declared`);
            bindings.set(name, { used: false });
        }

        // a counter operand is a counter or a parameter that names one
        for (const { operand } of node.bindings) {

            const parameter = bindings.get(operand);
            if (!parameter && !this.counters_.has(operand))
                fail(`undeclared counter operand '${operand}'`);
            if (parameter) parameter.used = true;
        }

        if (node.mode === 'loop') {

            if (!this.counters_.has(node.loopCounter!))
                fail(`undeclared counter '${node.loopCounter}'`);
            if (node.parameters.length !== 1 || node.bindings.length)
                fail('loop takes one parameter and no counter bindings');
            if (node.parts.some(part => typeof part !== 'string'
                && 'parts' in part)) fail('loop cannot contain snippets');
        }

        const children = new Set<string>();
        for (const part of node.parts) {

            if (typeof part === 'string') {

                if (node.mode === 'choice' && part.trim())
                    fail('choice may contain only alternatives and whitespace');
                continue;
            }
            if ('parameter' in part) {

                if (node.mode === 'choice')
                    fail('choice cannot contain substitutions', part.line);
                const binding = bindings.get(part.parameter);
                if (!binding) fail(`undeclared '${part.parameter}'`, part.line);
                binding!.used = true;
            }
            if ('parts' in part) {

                if (children.has(part.name) || bindings.has(part.name))
                    fail(`conflicting child '${part.name}'`, part.line);
                if (node.mode === 'choice' && part.mode !== 'one')
                    fail('choice alternatives must be single instances',
                        part.line);
                children.add(part.name);
                this.checkTemplate(part, bindings, `${path}.${part.name}`);
            }
        }
        if (node.mode === 'choice' && !children.size)
            fail('choice requires alternatives');
        for (const name of [...node.parameters, ...aliases]) {
            if (!bindings.get(name)!.used) fail(`unused parameter '${name}'`);
        }
    }

    private checkData(
        node: ShaderTemplate.Snippet,
        value: ShaderTemplate.Value | undefined,
        path: string,
    ): void {

        const fail = (message: string): never => {
            throw new Error(`${path}: ${message}`);
        };
        const records = this.instances(value);
        const count = records.length;

        // a loop takes its instance count from a counter, not from data
        if (node.mode === 'loop') {

            if (count !== 0) fail('loop takes no data');
            return;
        }
        if ((node.mode === 'one' || node.mode === 'choice') && count !== 1)
            fail(`expected exactly one instance, got ${count}`);
        if (node.mode === 'optional' && count > 1)
            fail(`expected zero or one instance, got ${count}`);
        if (node.mode === 'multi required' && count === 0)
            fail('expected at least one instance, got 0');

        const children = node.parts.filter(
            (part): part is ShaderTemplate.Snippet =>
                typeof part !== 'string' && 'parts' in part);
        const keys = new Set([...node.parameters,
            ...children.map(child => child.name)]);

        for (const [index, record] of records.entries()) {

            const at = node.mode.startsWith('multi')
                ? `${path}[${index}]` : path;
            const error = (message: string): never => {
                throw new Error(`${at}: ${message}`);
            };
            if (record === null || typeof record !== 'object'
                || Array.isArray(record)) error('expected a data record');
            const data = record as ShaderTemplate.Data;
            for (const name of Object.keys(data)) {
                if (!keys.has(name)) error(`unexpected key '${name}'`);
            }
            for (const name of node.parameters) {

                const scalar = Object.prototype.hasOwnProperty.call(data, name)
                    ? data[name] : undefined;
                if (typeof scalar !== 'string' && typeof scalar !== 'number'
                    && typeof scalar !== 'boolean')
                    error(`expected scalar parameter '${name}'`);
            }

            const supplied = (name: string): ShaderTemplate.Value | undefined =>
                Object.prototype.hasOwnProperty.call(data, name)
                    ? data[name] : undefined;
            if (node.mode === 'choice') {

                const selected = children.filter(child =>
                    this.instances(supplied(child.name)).length > 0);
                if (selected.length !== 1)
                    error(`expected exactly one child, got ${selected.length}`);
                const child = selected[0];
                this.checkData(child, supplied(child.name),
                    `${at}.${child.name}`);
            }
            if (node.mode !== 'choice') {

                for (const child of children)
                    this.checkData(child, supplied(child.name),
                        `${at}.${child.name}`);
            }
        } // records
    }

    /** Follow block comments across lines; returns the state at line end. */
    private blockComment(text: string, commented: boolean): boolean {

        let offset = 0;
        while (offset < text.length) {

            if (commented) {

                const blockClose = text.indexOf('*/', offset);
                if (blockClose < 0) return true;
                commented = false;
                offset = blockClose + 2;
                continue;
            }

            // a line comment hides the rest of the line, '/*' included
            const lineComment = text.indexOf('//', offset);
            const blockOpen = text.indexOf('/*', offset);
            if (blockOpen < 0) return false;
            if (lineComment >= 0 && lineComment < blockOpen) return false;
            commented = true;
            offset = blockOpen + 2;
        }
        return commented;
    }

    /** Parse `alias = name++`, `alias = --name` or `alias = name - 1`. */
    private parseBinding(
        clause: string, fail: (message: string) => never,
    ): ShaderTemplate.Binding {

        const match = clause.trim().match(
            /^(\w+)\s*=\s*(--)?([A-Za-z_]\w*)(\+\+|\s*-\s*1)?$/);
        if (!match || !!match[2] === !!match[4])
            fail(`invalid counter binding '${clause.trim()}'`);

        const [, alias, decrement, operand, suffix] = match!;
        let step: ShaderTemplate.Binding['step'] = '-1';
        if (decrement) step = '--';
        if (suffix === '++') step = '++';
        return { alias, operand, step };
    }

    private instances(
        value: ShaderTemplate.Value | undefined,
    ): ShaderTemplate.Value[] {

        if (value === undefined) return [];
        return Array.isArray(value) ? value : [value];
    }

    private readonly root_: ShaderTemplate.Snippet = {
        name: 'root', line: 1, mode: 'one', parameters: [], bindings: [],
        parts: [],
    };

    // declared counters and their initial values
    private readonly counters_ = new Map<string, number>();
}

namespace ShaderTemplate {

    export type Value = string | number | boolean | Data | Data[];
    export type Data = { [name: string]: Value | undefined };
    export type Mode =
        'one' | 'optional' | 'multi' | 'multi required' | 'choice' | 'loop';

    /** A counter read, and optionally changed, when a snippet is entered. */
    export type Binding = {
        alias: string;
        operand: string;
        step: '++' | '--' | '-1';
    };
    export type Snippet = {
        name: string;
        line: number;
        mode: Mode;
        parameters: string[];
        bindings: Binding[];
        loopCounter?: string;
        parts: (string | { parameter: string; line: number } | Snippet)[];
    };
}

export default ShaderTemplate;
